import { Sidebar } from "primereact/sidebar";
import SidebarContent from "./SidebarContent";
import EmptyHistory from "./EmptyHistory";
import SuccessNotification from "../entities/address/SuccessNotification";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTenantAudit } from "~/api/tenants/hooks/useTenantAudit";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import "./sidebar.scss";

interface TenantAuditSidebarProps {
  heading: string;
  sidebarToggle: boolean;
  setSidebarToggle: (value: boolean) => void;
  tenantId: string;
  labels: () => {
    create: string;
    update: string;
    delete: string;
  };
  useAuditHook?: (
    tenantId: string,
    enabled: boolean,
  ) => {
    data: any;
    fetchNextPage: () => void;
    hasNextPage: boolean | undefined;
    isFetchingNextPage: boolean;
    isLoading: boolean;
    isFetching: boolean;
  };
}

const TenantAuditSidebar: React.FC<TenantAuditSidebarProps> = ({
  heading,
  sidebarToggle,
  setSidebarToggle,
  tenantId,
  labels,
  useAuditHook,
}) => {
  const queryClient = useQueryClient();

  // Clear cache and refetch fresh data when sidebar opens
  // This mimics the behavior of AuditSidebar which uses skipCache: true
  useEffect(() => {
    if (sidebarToggle && tenantId) {
      // Invalidate all audit queries to force fresh data fetch
      queryClient.invalidateQueries({
        queryKey: ["director-address-audit", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["director-audit", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-audit", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-entity-access-audit", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-user-assignments-audit", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["user-entity-access-audit", tenantId],
      });
    }
  }, [sidebarToggle, tenantId, queryClient]);

  // Use custom hook if provided, otherwise default to useTenantAudit
  const auditHook = useAuditHook || useTenantAudit;
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = auditHook(tenantId, sidebarToggle);

  // Flatten all pages into a single array
  const auditData = data?.pages.flatMap((page: any) => page.data) ?? [];

  // Skeleton data for loading state
  const SKELETON_DATA: null[] = Array(3).fill(null);

  // Show skeleton when:
  // 1. Initial load (isLoading = true, no data yet)
  // 2. Refetching after cache invalidation (isFetching = true, has cached data, but not fetching next page)
  const hasCachedData = auditData.length > 0;
  const isRefetching = isFetching && !isFetchingNextPage;
  const shouldShowSkeleton = isLoading || (isRefetching && hasCachedData);

  const customHeader = (
    <div className="sidebar-header flex align-items-center gap-1">
      <span className="pi pi-history history-icon" />
      <h3 className="m-0">{heading ? `History: ${heading}` : "History"}</h3>
    </div>
  );

  return (
    <div className="sidebar-main card flex justify-content-center">
      <Sidebar
        position="right"
        className="history__sidebar"
        header={customHeader}
        visible={sidebarToggle}
        onHide={() => setSidebarToggle(false)}
      >
        {shouldShowSkeleton ? (
          <div id="audit-sidebar-scroll">
            <SidebarContent
              data={SKELETON_DATA}
              labels={labels}
              nameLink=""
              endType=""
              isEntityAccessAudit={heading === "Customer Portfolio"}
            />
          </div>
        ) : auditData.length > 0 ? (
          <div id="audit-sidebar-scroll">
            <InfiniteScroll
              dataLength={auditData.length}
              next={fetchNextPage}
              hasMore={hasNextPage ?? false}
              loader={
                isFetchingNextPage ? (
                  <p className="p-4">Loading more data...</p>
                ) : null
              }
              endMessage={<SuccessNotification successMsg="All data loaded" />}
              scrollableTarget="audit-sidebar-scroll"
            >
              <SidebarContent
                data={auditData}
                labels={labels}
                nameLink=""
                endType=""
                isEntityAccessAudit={heading === "Customer Portfolio"}
              />
            </InfiniteScroll>
          </div>
        ) : (
          <EmptyHistory />
        )}
      </Sidebar>
    </div>
  );
};

export default TenantAuditSidebar;
